import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import LoginLayout from '@copilot/common/pages/layouts/login';
import RouteWithPermissions from '@copilot/common/router/routeWithPermissions';
import LoginPage from '@copilot/common/pages/login';
import { OrgRoleTitles, RoleGroups } from '@copilot/common/store/models/const/enum';
import PasswordRecoveryPage from '@copilot/common/pages/passwordRecovery';
import AppSnippets from '@copilot/common/components/snippets/app/appSnippets';
import { MOUSEFLOW_APP_ID } from '@copilot/common/components/snippets/const';
import RegistrationPage from '@copilot/common/pages/registration';
import ScrollToTop from '@copilot/common/router/scrollToTop';
import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from '@copilot/common/utils/authenticationProvider';
import { PersonalizedInsightsPageV2 } from '@copilot/common/pages/personalizedInsightsV2/personalizedInsightsV2Page';
import { TeamsPageSideBarContext } from '../context/teamsPageSideBarContext';
import { BlocklistUploadWizard } from '@copilot/common/pages/organizationDashboard/blocklist/blocklistUploadWizard';
import { USERPILOT_APP_KEY } from '@copilot/data/config/routes';
import { AccountsDashboardRouter } from './accountsDashboardRouter';
import { BaseAppRouter } from './baseAppRouter';
import B2CSignUpPage from '@copilot/common/pages/b2csignup';

const roleReq = RoleGroups.Member;

function Router() {
    const [callBackMap, setCallBackMap] = useState<Record<string, () => void>>({});

    function onRegisterContextCallback(funcName: string, func: () => void) {
        setCallBackMap({ ...callBackMap, [funcName]: func });
    }

    return (
        <TeamsPageSideBarContext.Provider
            value={{
                registerCallback: onRegisterContextCallback,
                callBackMap,
            }}
        >
            <ScrollToTop />
            <Routes>
                <Route
                    path="/signup"
                    element={
                        <LoginLayout>
                            <RouteWithPermissions
                                element={<B2CSignUpPage />}
                                allowedRoles={RoleGroups.Guest}
                                roleRedirectTo="/"
                            />
                        </LoginLayout>
                    }
                />
                <Route
                    path="/login"
                    element={
                        <UnauthenticatedTemplate>
                            <LoginLayout>
                                <RouteWithPermissions
                                    element={<LoginPage />}
                                    allowedRoles={RoleGroups.Guest}
                                    roleRedirectTo={{
                                        [OrgRoleTitles.Owner]: '/main',
                                        [OrgRoleTitles.Admin]: '/main',
                                        [OrgRoleTitles.Advanced]: '/inbox',
                                        [OrgRoleTitles.User]: '/inbox',
                                    }}
                                />
                            </LoginLayout>
                        </UnauthenticatedTemplate>
                    }
                />
                <Route
                    path="/passwordrecovery"
                    element={
                        <UnauthenticatedTemplate>
                            <LoginLayout>
                                <RouteWithPermissions
                                    element={<PasswordRecoveryPage isIndividualUser={false} />}
                                    allowedRoles={RoleGroups.Guest}
                                    roleRedirectTo="/login"
                                />
                            </LoginLayout>
                        </UnauthenticatedTemplate>
                    }
                />
                <Route
                    path="/registration/passwordconfirmation"
                    element={
                        <UnauthenticatedTemplate>
                            <LoginLayout>
                                <RouteWithPermissions
                                    element={<RegistrationPage />}
                                    allowedRoles={RoleGroups.Guest}
                                    roleRedirectTo="/"
                                />
                            </LoginLayout>
                        </UnauthenticatedTemplate>
                    }
                />
                <Route
                    path="/dashboard/*"
                    element={<AccountsDashboardRouter roleReq={roleReq} />}
                />
                {/* TODO SPLIT THIS INTO A DISCRETE APP*/}
                <Route
                    path="/insights/:profileId"
                    element={
                        <AuthenticatedTemplate>
                            <RouteWithPermissions
                                element={<PersonalizedInsightsPageV2 />}
                                allowedRoles={roleReq}
                            />
                        </AuthenticatedTemplate>
                    }
                />
                <Route
                    path="/insights/connection/:connectionId"
                    element={
                        <AuthenticatedTemplate>
                            <RouteWithPermissions
                                element={<PersonalizedInsightsPageV2 />}
                                allowedRoles={roleReq}
                            />
                        </AuthenticatedTemplate>
                    }
                />
                {/* TODO DELETE THIS ROUTE AND ASSOCIATED COMPONENT */}
                <Route
                    path="/wizard/blocklist"
                    element={
                        <AuthenticatedTemplate>
                            <RouteWithPermissions
                                element={<BlocklistUploadWizard />}
                                allowedRoles={RoleGroups.SysAdmin}
                            />
                        </AuthenticatedTemplate>
                    }
                />
                <Route path="/*" element={<BaseAppRouter roleReq={roleReq} />} />
            </Routes>
            <AppSnippets
                isTeamUser
                mouseflowAppId={MOUSEFLOW_APP_ID}
                userpilotAppKey={USERPILOT_APP_KEY}
            />
        </TeamsPageSideBarContext.Provider>
    );
}

export default Router;
